import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { EventListProps } from './types';
import { STYLE_VARS } from '../../constants/style-vars';
import { Image } from '../../components/Image';
import { Button } from '../../components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../services/redux/store';
import EventItem from './components/EventItem';
import { Footer } from '../../components/Footer';
import EventCalendar from './components/EventCalendar';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import PageTitle from '../../components/PageTitle';
import { EventsLayout } from '../../WebLayout';
import { IS_WEB } from '../../constants/platform';
import { addBase64Prefix } from '../../services/addBase64Prefix';
import { search_white } from '../../assets/base64';
import { TextInput } from '../../components/TextInput/TextInput';
import { COLORS, FONTS } from '../../constants';
import { EventsApi } from '../../services/api';
import { Loading } from '../../components';
import {
  EventFilter,
  clearEventFilter,
  setEventFilter,
  setEvents,
  setLoadingEvent,
  setTotal,
} from '../../services/redux/reducers/EventSlice';
import { debounce } from 'lodash';
import Filter from '../../components/Filter/Filter';
import { IEventCategory } from '../../types';
import { Category } from '../../types/categories.types';

const EvenList: FC<EventListProps> = ({ navigation, route }) => {
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();

  const { eventFilter, events, total, isLoading } = useSelector(
    (state: RootState) => state.eventReducer
  );

  const screen = route.params.screen;
  const [type, setType] = useState<'calendar' | 'past' | 'upcoming'>(
    'upcoming'
  );
  const [toggleFilter, setToggleFilter] = useState<boolean>(false);
  const [filterSelected, setFilterSelected] = useState<Category[]>([]);

  const getEvents = useCallback(
    async (props: EventFilter) => {
      try {
        dispatch(setLoadingEvent(true));
        const res = await EventsApi.getEvents(props);
        dispatch(setEvents(res.data.events));
        dispatch(setTotal(res.data.total));
      } catch (error: any) {
        console.log(error);
      } finally {
        dispatch(setLoadingEvent(false));
      }
    },
    [dispatch]
  );

  const handleLoadmore = useCallback(async () => {
    if (events.length < total) {
      dispatch(
        setEventFilter({
          ...eventFilter,
          page: Math.ceil(events.length / 10) + 1,
        })
      );
      try {
        dispatch(setLoadingEvent(true));
        const res = await EventsApi.getEvents({
          ...eventFilter,
          page: Math.ceil(events.length / 10) + 1,
        });
        dispatch(setEvents([...events, ...res.data.events]));
        dispatch(setTotal(res.data.total));
      } catch (error: any) {
        console.log(error);
      } finally {
        dispatch(setLoadingEvent(false));
      }
    }
  }, [events, total, dispatch, eventFilter]);

  useEffect(() => {
    dispatch(clearEventFilter());
    getEvents({ view: type });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, getEvents]);

  const buttons = useMemo(() => {
    return [
      {
        name: 'List',
        onPress: () => {
          navigation.setParams({ screen: 'List' });
        },
        icon: require('../../assets/event-list.png'),
      },
      {
        name: 'Grid',
        onPress: () => {
          navigation.setParams({ screen: 'Grid' });
        },
        icon: require('../../assets/event-grid.png'),
      },
      // {
      //   name: 'Calendar',
      //   onPress: () => {
      //     navigation.setParams({ screen: 'Calendar' });
      //   },
      //   icon: require('../../assets/bookings-white.png'),
      // },
    ];
  }, [navigation]);

  const upcommingEventButton = useMemo(() => {
    return (
      <Button
        text="UPCOMMING EVENTS"
        className={type === 'upcoming' ? 'green-button' : ''}
        style={
          type === 'upcoming'
            ? STYLE_VARS.fullWGreenButton
            : STYLE_VARS.unActiveButton
        }
        arrowStyle={
          type !== 'upcoming' && {
            tintColor: STYLE_VARS.primaryColor,
          }
        }
        textStyle={{
          color: type === 'upcoming' ? 'white' : STYLE_VARS.textColor,
        }}
        onPress={() => {
          dispatch(setEvents([]));
          setType('upcoming');
          getEvents({
            ...eventFilter,
            view: 'upcoming',
            page: 1,
          });
          dispatch(
            setEventFilter({
              ...eventFilter,
              view: 'upcoming',
              page: 1,
            })
          );
        }}
      />
    );
  }, [type, dispatch, eventFilter, getEvents]);

  const paseEventButton = useMemo(() => {
    return (
      <Button
        text="PAST EVENTS"
        className={type === 'past' ? 'green-button' : ''}
        arrowStyle={
          type !== 'past' && {
            tintColor: STYLE_VARS.primaryColor,
          }
        }
        style={
          type === 'past'
            ? STYLE_VARS.fullWGreenButton
            : STYLE_VARS.unActiveButton
        }
        textStyle={{
          color: type === 'past' ? 'white' : STYLE_VARS.textColor,
        }}
        onPress={() => {
          setType('past');
          dispatch(setEvents([]));
          getEvents({
            ...eventFilter,
            view: 'past',
            page: 1,
          });
          dispatch(
            setEventFilter({
              ...eventFilter,
              view: 'past',
              page: 1,
            })
          );
        }}
      />
    );
  }, [type, dispatch, eventFilter, getEvents]);

  const handleSearch = useCallback(
    (text: string) => {
      dispatch(setEvents([]));
      getEvents({
        ...eventFilter,
        page: 1,
        search: text,
        view: type,
      });
      dispatch(
        setEventFilter({
          ...eventFilter,
          page: 1,
          search: text,
          view: type,
        })
      );
    },
    [eventFilter, dispatch, getEvents, type]
  );

  const debouncedSearch = debounce(handleSearch, 500);
  const [eventCategories, setEventCategories] = useState<IEventCategory[]>([]);

  const getEventCategoriesData = useCallback(async () => {
    try {
      const res = await EventsApi.getEventsCategory({});
      setEventCategories(res.data);
    } catch (error: any) {
      console.log(error);
    } finally {
    }
  }, []);

  useEffect(() => {
    getEventCategoriesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isDesktop()) {
    return (
      <EventsLayout>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            borderBottomColor: STYLE_VARS.fccDarkGrey,
            borderBottomWidth: 1,
            paddingBottom: 20,
          }}
        >
          <View
            style={{
              alignItems: 'center',
              flexDirection: 'row',
              display: 'flex',
              gap: 10,
              width: '40%',
              minWidth: 350,
            }}
          >
            <View style={{ flex: 1 }}>{upcommingEventButton}</View>
            <View style={{ flex: 1 }}>{paseEventButton}</View>
          </View>
          <View style={{ flexDirection: 'row' }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginEnd: 30,
              }}
            >
              <TextInput
                placeholder="SEARCH"
                placeholderTextColor={STYLE_VARS.primaryColor}
                onChangeText={debouncedSearch}
                style={{
                  fontFamily: FONTS.IowanOldStyleBTRoman,
                  fontSize: 13,
                  color: STYLE_VARS.primaryColor,
                  width: 70,
                  marginEnd: 5,
                }}
              />
              <Image
                source={
                  IS_WEB
                    ? addBase64Prefix(search_white)
                    : require('../../assets/search_white.png')
                }
                style={{
                  width: 20,
                  height: 20,
                  tintColor: STYLE_VARS.primaryColor,
                  marginEnd: 5,
                }}
              />
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {buttons.map((button) => (
                <TouchableOpacity
                  style={{ padding: 2 }}
                  key={button.name}
                  onPress={button.onPress}
                >
                  <Image
                    source={button.icon}
                    style={{
                      width: 15,
                      height: 15,
                      tintColor:
                        screen === button.name
                          ? STYLE_VARS.primaryColor
                          : STYLE_VARS.fccDarkGrey,
                      resizeMode: 'contain',
                    }}
                  />
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
        <View>
          {screen === 'Calendar' ? (
            <View style={[styles.container, { marginBottom: 50 }]}>
              <EventCalendar
                eventDays={[
                  {
                    start: '2023-06-24',
                    end: '2023-06-30',
                    title: 'Take away special - OCT',
                  },
                ]}
              />
            </View>
          ) : (
            <>
              <View>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  {events &&
                    events.map((event, index) => (
                      <View
                        style={
                          screen === 'Grid'
                            ? { width: '100%' }
                            : { width: '50%' }
                        }
                        key={event.id}
                      >
                        <EventItem
                          item={event}
                          type={screen}
                          key={event.id}
                          haveBottomLine={
                            index + 1 > 2 * Math.round(events.length / 2 - 1) &&
                            screen === 'List'
                              ? false
                              : true
                          }
                        />
                      </View>
                    ))}
                </View>
              </View>
              {isLoading === false && events.length === 0 ? (
                <View style={styles.noDataWrapper}>
                  <Text style={[styles.noDataText, { fontSize: 20 }]}>
                    THERE ARE CURRENTLY NO EVENTS
                  </Text>
                </View>
              ) : (
                <></>
              )}
              {isLoading && <Loading style={{ paddingVertical: 30 }} />}
              {total > events.length && (
                <View
                  style={{
                    marginTop: 30,
                    marginBottom: 50,
                    alignSelf: 'center',
                  }}
                >
                  <Button
                    text="VIEW MORE"
                    style={STYLE_VARS.blueButton}
                    onPress={handleLoadmore}
                    disabled={isLoading}
                  />
                </View>
              )}
            </>
          )}
        </View>
      </EventsLayout>
    );
  }

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View style={styles.container}>
        <PageTitle
          title="Events"
          icon={require('../../assets/events.png')}
          haveGoBackButton={true}
        />
      </View>
      <View style={styles.container}>
        <View
          style={{
            paddingVertical: 20,
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          {upcommingEventButton}
          {paseEventButton}
        </View>
      </View>
      <View style={[styles.container, { zIndex: 5 }]}>
        <View
          style={[
            styles.row,
            { justifyContent: 'space-between', marginBottom: 25 },
          ]}
        >
          <TouchableOpacity
            style={[
              styles.row,
              {
                justifyContent: 'flex-start',
              },
            ]}
            onPress={() => {
              setToggleFilter(!toggleFilter);
            }}
          >
            <Text style={styles.textGreen}>Filter</Text>
            <Image
              source={require('../../assets/filter-button.png')}
              style={{
                width: 15,
                height: 15,
                resizeMode: 'contain',
                marginLeft: 5,
              }}
            />
          </TouchableOpacity>
          <View
            style={[styles.row, { width: '30%', justifyContent: 'center' }]}
          >
            <View style={{ width: 80 }}>
              <TextInput
                placeholder="SEARCH"
                placeholderTextColor={STYLE_VARS.primaryColor}
                onChangeText={debouncedSearch}
                style={{
                  fontSize: 16,
                  fontWeight: '500',
                  color: STYLE_VARS.primaryColor,
                  fontFamily: FONTS.IowanOldStyleBTRoman,
                  paddingHorizontal: 6,
                  paddingVertical: 1,
                  flex: 1,
                }}
              />
            </View>
            {/* <Text style={styles.textGreen}>Search</Text> */}
            <Image
              source={require('../../assets/search_white.png')}
              style={{
                width: 15,
                height: 15,
                resizeMode: 'contain',
                tintColor: STYLE_VARS.primaryColor,
                marginLeft: 5,
              }}
            />
          </View>
          <View
            style={[
              styles.row,
              { width: '30%', justifyContent: 'flex-end', gap: 10 },
            ]}
          >
            {buttons.map((button) => (
              <TouchableOpacity
                style={{ padding: 2 }}
                key={button.name}
                onPress={button.onPress}
              >
                <Image
                  source={button.icon}
                  style={{
                    width: 15,
                    height: 15,
                    tintColor:
                      screen === button.name
                        ? STYLE_VARS.primaryColor
                        : STYLE_VARS.fccDarkGrey,
                    resizeMode: 'contain',
                  }}
                />
              </TouchableOpacity>
            ))}
          </View>
        </View>
        {toggleFilter && (
          <Filter
            categoriesData={eventCategories}
            getData={getEvents}
            filterData={eventFilter}
            setFilterData={setEventFilter}
            setData={setEvents}
            filterSelected={filterSelected}
            setFilterSelected={setFilterSelected}
          />
        )}
      </View>
      {screen === 'Calendar' ? (
        <View style={[styles.container, { marginBottom: 50 }]}>
          <EventCalendar
            eventDays={[
              {
                start: '2023-06-24',
                end: '2023-06-30',
                title: 'Take away special - OCT',
              },
            ]}
          />
        </View>
      ) : (
        <>
          <View style={styles.container}>
            {events &&
              events?.map((event) => (
                <EventItem item={event} type={screen} key={event.id} />
              ))}
          </View>
          {isLoading === false && events.length === 0 ? (
            <View style={styles.noDataWrapper}>
              <Text style={[styles.noDataText, { fontSize: 16 }]}>
                THERE ARE CURRENTLY NO EVENTS
              </Text>
            </View>
          ) : (
            <></>
          )}
          {isLoading && <Loading style={{ paddingVertical: 30 }} />}
          {total > events.length && (
            <View style={{ marginTop: 30, marginBottom: 50 }}>
              <Button
                text="VIEW MORE"
                style={STYLE_VARS.blueButton}
                onPress={handleLoadmore}
                disabled={isLoading}
              />
            </View>
          )}
        </>
      )}
      <Footer />
    </ScrollView>
  );
};

export default EvenList;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
  },
  textGreen: {
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontSize: 16,
    fontWeight: '500',
    color: STYLE_VARS.textColor,
    textTransform: 'uppercase',
  },
  arrowButton: {
    padding: 5,
  },
  arrowStyle: {
    height: 13,
    width: 10,
    resizeMode: 'contain',
    tintColor: STYLE_VARS.primaryColor,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  noDataWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  noDataText: {
    color: COLORS.GREEN,
    textTransform: 'uppercase',
    fontFamily: FONTS.AvenirMedium,
  },
});
